import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useTheme } from '../hooks/useTheme';
import ExpiredCustomersTable from '../components/ExpiredCustomersTable';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Calendar, Clock, AlertTriangle, MessageCircle, CheckCircle,
  ChevronRight, ChevronDown, TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// ── Constants ─────────────────────────────────────────────────────────────────
const METHOD_COLORS = {
  cash:      '#10b981',
  jazzcash:  '#f97316',
  easypaisa: '#22c55e',
  bank:      '#3b82f6',
};
const METHOD_LABELS = {
  cash: 'Cash', jazzcash: 'JazzCash', easypaisa: 'EasyPaisa', bank: 'Bank',
};
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const getChartColors = (isDark) => ({
  grid:      isDark ? '#334155' : '#f3f4f6',
  axisText:  isDark ? '#94a3b8' : '#9ca3af',
  axisLabel: isDark ? '#cbd5e1' : '#374151',
  cursor:    isDark ? '#334155' : '#f3f4f6',
  tooltipBg:     isDark ? '#1e293b' : '#ffffff',
  tooltipBorder: isDark ? '#334155' : '#f3f4f6',
  tooltipText:   isDark ? '#f1f5f9' : '#374151',
  tooltipLabel:  isDark ? '#e2e8f0' : '#374151',
  tooltipMuted:  isDark ? '#94a3b8' : '#6b7280',
});

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const PKRTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={`${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-100'} shadow-xl border rounded-xl p-3 text-xs min-w-[150px]`}>
      <p className={`font-bold ${isDark ? 'text-slate-200' : 'text-gray-700'} mb-2`}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-3 py-0.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: p.color }} />
            <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>{METHOD_LABELS[p.dataKey] || p.name}</span>
          </div>
          <span className={`font-bold ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>PKR {Number(p.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

// ── Expiry Alert Column ───────────────────────────────────────────────────────
const ExpiryColumn = ({ title, data, colorTheme, icon: Icon, daysLabel, onSendReminder }) => {
  const themes = {
    red:    { headerBg: 'bg-red-50',    borderColor: 'border-red-100',    textColor: 'text-red-800',    badgeBg: 'bg-red-200',    badgeText: 'text-red-800',    btnHover: 'hover:bg-red-50 hover:border-red-200 hover:text-red-600' },
    orange: { headerBg: 'bg-orange-50', borderColor: 'border-orange-100', textColor: 'text-orange-800', badgeBg: 'bg-orange-200', badgeText: 'text-orange-800', btnHover: 'hover:bg-orange-50 hover:border-orange-200 hover:text-orange-600' },
    blue:   { headerBg: 'bg-blue-50',   borderColor: 'border-blue-100',   textColor: 'text-blue-800',   badgeBg: 'bg-blue-200',   badgeText: 'text-blue-800',   btnHover: 'hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600' },
  };
  const theme = themes[colorTheme];
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full">
      <div className={`p-3 sm:p-4 border-b flex justify-between items-center ${theme.headerBg} ${theme.borderColor}`}>
        <h3 className={`font-bold text-sm sm:text-base flex items-center gap-2 ${theme.textColor}`}>
          <Icon size={16} /> {title}
        </h3>
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${theme.badgeBg} ${theme.badgeText}`}>{data.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[400px] p-3 space-y-3">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400">
            <CheckCircle size={32} className="mb-2 opacity-50" />
            <p className="text-sm">No customers expire soon.</p>
          </div>
        ) : (
          data.map(cust => (
            <div key={cust.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-white hover:shadow-md transition-all group">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-gray-800 text-sm">{cust.username}</p>
                  <p className="text-xs text-gray-500 truncate w-32">{cust.name}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Pkg</span>
                    <span className="text-xs font-medium text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200">{cust.package__name || 'N/A'}</span>
                  </div>
                  <p className="text-xs text-red-500 font-semibold mt-1 flex items-center gap-1">
                    <Clock size={12} /> Expires: {cust.expiry_date}
                  </p>
                </div>
                <button
                  onClick={() => onSendReminder(cust.phone, cust.name, daysLabel, cust.username)}
                  disabled={!cust.phone}
                  className={`p-2 bg-white border border-gray-200 rounded-lg text-green-600 transition-colors ${theme.btnHover} disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={`Send Reminder to ${cust.name}`}
                >
                  <MessageCircle size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ── Section header ────────────────────────────────────────────────────────────
const SectionTitle = ({ color, children, action }) => (
  <div className="flex items-center justify-between mb-3 sm:mb-4">
    <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
      <span className={`w-1 h-6 ${color} rounded-full`} />
      {children}
    </h2>
    {action}
  </div>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { isDark } = useTheme();
  const chartColors = getChartColors(isDark);
  const [data, setData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [expiryData, setExpiryData] = useState({ expiring_tomorrow: [], expiring_in_3_days: [], expiring_in_7_days: [] });
  const [loading, setLoading] = useState(true);
  const [monthExpanded, setMonthExpanded] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [overviewRes, monthRes] = await Promise.all([
          api.get('/admin/dashboard-analytics/?period=overview'),
          api.get('/admin/dashboard-analytics/?period=month'),
        ]);
        setData(overviewRes.data);
        setMonthData(monthRes.data);
        api.get('/expiry-alerts/').then(r => setExpiryData(r.data)).catch(() => {});
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sendReminder = async (phone, name, daysLabel, username) => {
    if (!phone) { alert('Customer has no phone number.'); return; }
    if (!window.confirm(`Send WhatsApp reminder to ${name} for expiring in ${daysLabel}?`)) return;
    try {
      const res = await api.post('/send-manual-reminder/', { phone, name, username: username || '', daysLabel });
      if (res.data.success) alert(`✅ Reminder sent to ${name}!`);
      else alert('❌ Failed to send reminder. Check logs.');
    } catch (err) {
      console.error(err);
      alert('❌ Error sending message.');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>
  );

  const collections = data?.collections || {};
  const customers   = data?.customers   || {};
  const complaints  = data?.complaints  || {};

  const todayPct = collections.today_expected > 0
    ? Math.round((collections.today_collected / collections.today_expected) * 100) : 0;

  // ── Chart data ──────────────────────────────────────────────────────────────
  const dailyData = (monthData?.daily_breakdown || []).map(d => {
    const monthIdx = parseInt(d.date.slice(5, 7), 10) - 1;
    const day = d.date.slice(-2).replace(/^0/, '');
    return {
      day: `${day} ${MONTH_NAMES[monthIdx]}`,
      cash:      d.by_method?.cash      || 0,
      jazzcash:  d.by_method?.jazzcash  || 0,
      easypaisa: d.by_method?.easypaisa || 0,
      bank:      d.by_method?.bank      || 0,
    };
  });

  const yearlyData = (monthData?.yearly_snapshot || []).map(y => ({
    month: MONTH_NAMES[parseInt(y.month.slice(5, 7), 10) - 1],
    total: y.total_collected,
  }));

  const byMethod = monthData?.by_method || {};
  const methodPieData = Object.entries(byMethod)
    .map(([key, value]) => ({ name: METHOD_LABELS[key] || key, value, color: METHOD_COLORS[key] || '#6b7280' }))
    .filter(d => d.value > 0);
  const methodTotal = methodPieData.reduce((s, d) => s + d.value, 0);

  const officerBarData = (monthData?.by_officer || []).slice(0, 7).map(o => ({
    name: o.officer_name.length > 10 ? o.officer_name.slice(0, 10) + '…' : o.officer_name,
    collected: Math.round(o.collected),
    count: o.count,
  }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen space-y-6 sm:space-y-8">

      {/* ── Hero: Today's Collection ─────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #6A1B9A 0%, #9C27B0 100%)' }}
        className="rounded-2xl p-5 sm:p-8 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-purple-100 text-sm font-medium mb-1">Today's Collection</p>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
              PKR {(collections.today_collected || 0).toLocaleString()}
            </h1>
            <p className="text-purple-100 text-sm mt-1">
              of PKR {Math.round(collections.today_expected || 0).toLocaleString()} expected
              <span className="mx-2 opacity-40">|</span>
              {collections.today_count || 0} entries
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`text-2xl sm:text-3xl font-extrabold px-4 py-2 rounded-xl ${
              todayPct >= 80 ? 'bg-emerald-500/40' :
              todayPct >= 50 ? 'bg-amber-400/40' : 'bg-red-400/40'
            }`}>
              {todayPct}%
            </div>
            <Link to="/dashboard/today" className="flex items-center gap-1 text-sm text-purple-100 hover:text-white transition-colors">
              Details <ChevronRight size={16} />
            </Link>
          </div>
        </div>
        <div className="mt-5 sm:mt-6">
          <div className="w-full h-3 sm:h-4 bg-white/20 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-1000 ease-out ${
              todayPct >= 80 ? 'bg-emerald-400' :
              todayPct >= 50 ? 'bg-amber-400' : 'bg-red-400'
            }`} style={{ width: `${Math.min(todayPct, 100)}%` }} />
          </div>
        </div>
      </div>

      {/* ── Quick Stats: 3 cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100 border-l-4 border-l-[#7B1FA2] shadow-sm">
          <p className="text-xs text-gray-500 font-medium">Today's Entries</p>
          <p className="text-2xl font-extrabold text-gray-900">{collections.today_count || 0}</p>
          <p className="text-xs text-gray-400 mt-0.5">{customers.today_activations || 0} new activations</p>
        </div>

        <Link to="/customers" className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100 border-l-4 border-l-[#9C27B0] shadow-sm hover:shadow-md transition-shadow">
          <p className="text-xs text-gray-500 font-medium">Active Customers</p>
          <p className="text-2xl font-extrabold text-gray-900">{customers.active || 0}</p>
          {customers.expiring_tomorrow > 0 && (
            <p className="text-xs text-red-500 font-medium mt-0.5">{customers.expiring_tomorrow} expiring tomorrow</p>
          )}
        </Link>

        <div className={`bg-white rounded-xl p-4 sm:p-5 border border-gray-100 border-l-4 shadow-sm ${
          collections.collection_rate >= 80 ? 'border-l-emerald-500' :
          collections.collection_rate >= 50 ? 'border-l-amber-500' : 'border-l-red-500'
        }`}>
          <p className="text-xs text-gray-500 font-medium">Month Collection Rate</p>
          <p className={`text-2xl font-extrabold ${
            collections.collection_rate >= 80 ? 'text-emerald-700' :
            collections.collection_rate >= 50 ? 'text-amber-700' : 'text-red-700'
          }`}>{collections.collection_rate || 0}%</p>
          <div className="mt-2 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${
              collections.collection_rate >= 80 ? 'bg-emerald-500' :
              collections.collection_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
            }`} style={{ width: `${Math.min(collections.collection_rate || 0, 100)}%` }} />
          </div>
        </div>
      </div>

      {/* ── Monthly Overview — Collapsible ───────────────────────────────── */}
      <div>
        <button
          onClick={() => setMonthExpanded(!monthExpanded)}
          className="w-full flex items-center justify-between mb-3 sm:mb-4 group cursor-pointer"
        >
          <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
            <span className="w-1 h-6 bg-[#7B1FA2] rounded-full" />
            This Month
          </h2>
          <div className="flex items-center gap-2">
            <Link
              to="/dashboard/month"
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-medium text-[#7B1FA2] hover:text-[#6A1B9A] flex items-center gap-1"
            >
              View details <ChevronRight size={14} />
            </Link>
            <ChevronDown
              size={18}
              className={`text-gray-400 transition-transform duration-200 ${monthExpanded ? '' : '-rotate-90'}`}
            />
          </div>
        </button>

        {monthExpanded && (
          <>
            {/* ── Collection subsection ─────────────────────────────────── */}
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Collection</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 border border-gray-100 border-l-4 border-l-emerald-500 shadow-sm">
                <p className="text-xs text-gray-500 font-medium">Collected</p>
                <p className="text-xl sm:text-2xl font-extrabold text-gray-900">PKR {(collections.month_collected || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">{collections.month_count || 0} entries</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100 border-l-4 border-l-amber-500 shadow-sm">
                <p className="text-xs text-gray-500 font-medium">Expected</p>
                <p className="text-xl sm:text-2xl font-extrabold text-gray-900">PKR {Math.round(collections.month_expected || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">target revenue</p>
              </div>
              <div className={`bg-white rounded-xl p-4 border border-gray-100 border-l-4 shadow-sm ${
                collections.collection_rate >= 80 ? 'border-l-green-500' :
                collections.collection_rate >= 50 ? 'border-l-amber-500' : 'border-l-red-500'
              }`}>
                <p className="text-xs text-gray-500 font-medium">Rate</p>
                <p className={`text-xl sm:text-2xl font-extrabold ${
                  collections.collection_rate >= 80 ? 'text-green-700' :
                  collections.collection_rate >= 50 ? 'text-amber-700' : 'text-red-700'
                }`}>{collections.collection_rate || 0}%</p>
                <div className="mt-2 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${
                    collections.collection_rate >= 80 ? 'bg-green-500' :
                    collections.collection_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                  }`} style={{ width: `${Math.min(collections.collection_rate || 0, 100)}%` }} />
                </div>
              </div>
            </div>

            {/* ── Packages subsection ──────────────────────────────────── */}
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Packages</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-white rounded-xl p-4 border border-gray-100 border-l-4 border-l-blue-500 shadow-sm">
                <p className="text-xs text-gray-500 font-medium">Activated</p>
                <p className="text-xl sm:text-2xl font-extrabold text-gray-900">{customers.activated_this_month || 0}</p>
                <p className="text-xs text-gray-400 mt-1">packages this month</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100 border-l-4 border-l-cyan-500 shadow-sm">
                <p className="text-xs text-gray-500 font-medium">New Customers</p>
                <p className="text-xl sm:text-2xl font-extrabold text-gray-900">{customers.new_customers_this_month || 0}</p>
                <p className="text-xs text-gray-400 mt-1">joined this month</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100 border-l-4 border-l-rose-500 shadow-sm">
                <p className="text-xs text-gray-500 font-medium">Missed</p>
                <p className="text-xl sm:text-2xl font-extrabold text-gray-900">{customers.missed_this_month || 0}</p>
                <p className="text-xs text-gray-400 mt-1">packages not renewed</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100 border-l-4 border-l-red-500 shadow-sm">
                <p className="text-xs text-gray-500 font-medium">Expiring 7d</p>
                <p className="text-xl sm:text-2xl font-extrabold text-gray-900">{customers.expiring_in_7_days || 0}</p>
                <p className="text-xs text-gray-400 mt-1">customers at risk</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Charts Row 1: Daily Collections + Payment Methods ─────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">

        {/* Daily Collections — stacked bar */}
        <div className="lg:col-span-2 bg-white rounded-xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <SectionTitle color="bg-[#7B1FA2]">Daily Collections — This Month</SectionTitle>
            <span className="text-xs text-gray-400 -mt-3.5">{dailyData.length} days</span>
          </div>
          {dailyData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dailyData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                  barSize={dailyData.length > 20 ? 7 : 14}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: chartColors.axisText }} tickLine={false} axisLine={false} interval={dailyData.length > 15 ? 2 : 0} />
                  <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 10, fill: chartColors.axisText }} tickLine={false} axisLine={false} />
                  <Tooltip content={<PKRTooltip isDark={isDark} />} cursor={{ fill: chartColors.cursor }} />
                  <Bar dataKey="cash"      stackId="a" fill={METHOD_COLORS.cash} />
                  <Bar dataKey="jazzcash"  stackId="a" fill={METHOD_COLORS.jazzcash} />
                  <Bar dataKey="easypaisa" stackId="a" fill={METHOD_COLORS.easypaisa} />
                  <Bar dataKey="bank"      stackId="a" fill={METHOD_COLORS.bank} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
                {Object.entries(METHOD_COLORS).map(([key, color]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
                    <span className="text-[11px] text-gray-500">{METHOD_LABELS[key]}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-52 flex items-center justify-center text-gray-400 text-sm">
              No collections recorded this month yet.
            </div>
          )}
        </div>

        {/* Payment Methods — donut */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="mb-1">
            <SectionTitle color="bg-[#9C27B0]">By Method — This Month</SectionTitle>
          </div>
          {methodPieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={165}>
                <PieChart>
                  <Pie data={methodPieData} cx="50%" cy="50%"
                    innerRadius={48} outerRadius={72}
                    dataKey="value" paddingAngle={3} startAngle={90} endAngle={-270}>
                    {methodPieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [`PKR ${Number(v).toLocaleString()}`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-1">
                {methodPieData.map(m => {
                  const pct = methodTotal > 0 ? Math.round((m.value / methodTotal) * 100) : 0;
                  return (
                    <div key={m.name} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: m.color }} />
                      <span className="text-xs text-gray-600 flex-1">{m.name}</span>
                      <span className="text-xs font-bold text-gray-700">{pct}%</span>
                      <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: m.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No data.</div>
          )}
        </div>
      </div>

      {/* ── Charts Row 2: Yearly Revenue + Officer Performance ────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">

        {/* Yearly Revenue — area chart */}
        <div className="lg:col-span-2 bg-white rounded-xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="mb-1">
            <SectionTitle color="bg-[#7B1FA2]"
              action={<span className="text-xs text-gray-400 -mt-3.5">{new Date().getFullYear()}</span>}>
              Monthly Revenue Trend
            </SectionTitle>
          </div>
          {yearlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={yearlyData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#7B1FA2" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#7B1FA2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: chartColors.axisText }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 10, fill: chartColors.axisText }} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(v) => [`PKR ${Number(v).toLocaleString()}`, 'Revenue']}
                  contentStyle={{ borderRadius: '12px', border: `1px solid ${chartColors.tooltipBorder}`, boxShadow: '0 10px 25px rgba(0,0,0,0.08)', fontSize: '12px', backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText }}
                />
                <Area type="monotone" dataKey="total" stroke="#7B1FA2" strokeWidth={2.5}
                  fill="url(#revGrad)"
                  dot={{ r: 4, fill: '#7B1FA2', strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: '#7B1FA2', strokeWidth: 2, stroke: '#fff' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-gray-400 text-sm">
              No annual data available yet.
            </div>
          )}
        </div>

        {/* Officer Performance — horizontal bar */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="mb-1">
            <SectionTitle color="bg-[#9C27B0]">Officers — This Month</SectionTitle>
          </div>
          {officerBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={officerBarData} layout="vertical"
                margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barSize={12}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} horizontal={false} />
                <XAxis type="number"
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
                  tick={{ fontSize: 9, fill: chartColors.axisText }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" width={68}
                  tick={{ fontSize: 11, fill: chartColors.axisLabel }} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(v) => [`PKR ${Number(v).toLocaleString()}`, 'Collected']}
                  contentStyle={{ borderRadius: '12px', border: `1px solid ${chartColors.tooltipBorder}`, boxShadow: '0 10px 25px rgba(0,0,0,0.08)', fontSize: '12px', backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText }}
                />
                <Bar dataKey="collected" fill="#9C27B0" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-gray-400 text-sm">
              No officer data this month.
            </div>
          )}
        </div>
      </div>

      {/* ── Complaints — single compact box ──────────────────────────────── */}
      <Link to="/complaints" className="block bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-5 bg-red-500 rounded-full" /> Complaints Overview
            </h2>
            <ChevronRight size={16} className="text-gray-400" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { dot: 'bg-red-500',    val: complaints.open        || 0, label: 'Open' },
              { dot: 'bg-amber-500',  val: complaints.in_progress || 0, label: 'In Progress' },
              { dot: 'bg-[#7B1FA2]', val: complaints.today_new   || 0, label: 'New Today' },
              { dot: 'bg-red-700',    val: complaints.overdue     || 0, label: 'Overdue' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item.dot}`} />
                <div>
                  <p className="text-lg font-extrabold text-gray-900">{item.val}</p>
                  <p className="text-[11px] text-gray-500">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
          {complaints.by_priority?.critical > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2">
              <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                {complaints.by_priority.critical} critical priority
              </span>
              {complaints.by_priority?.high > 0 && (
                <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                  {complaints.by_priority.high} high
                </span>
              )}
            </div>
          )}
        </div>
      </Link>

      {/* ── Expired Customers ─────────────────────────────────────────────── */}
      <div>
        <ExpiredCustomersTable customers={data?.expired_this_month || []} />
      </div>

      {/* ── Expiry Alerts ─────────────────────────────────────────────────── */}
      <div>
        <SectionTitle color="bg-red-600">Expiry Alerts</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <ExpiryColumn title="Expiring Tomorrow"   data={expiryData.expiring_tomorrow}   colorTheme="red"    icon={AlertTriangle} daysLabel="1 day"    onSendReminder={sendReminder} />
          <ExpiryColumn title="Expiring in 2-3 Days" data={expiryData.expiring_in_3_days} colorTheme="orange" icon={Clock}         daysLabel="2-3 days" onSendReminder={sendReminder} />
          <ExpiryColumn title="Expiring in 4-7 Days" data={expiryData.expiring_in_7_days} colorTheme="blue"   icon={Calendar}      daysLabel="4-7 days" onSendReminder={sendReminder} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
